import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Search,
  TrendingUp
} from '@mui/icons-material';
import DashboardService from '../../services/dashboard';
import DateInterval from '../../components/calendar/DateInterval';
import MetricCard from '../../components/MetricCard';

const ProfitabilityAnalysis = ({ storeId }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await DashboardService.getProfitabilityAnalysis(storeId, {
        ...dateRange,
        limit: 50
      });
      setData(result);
      setFilteredData(result);
    } catch (err) {
      setError('Error al cargar el análisis de rentabilidad');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = data.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [searchTerm, data]);

  const handleDateChange = (start, end) => {
    if (start && end) {
      setDateRange({
        date_from: start.format('YYYY-MM-DD'),
        date_to: end.format('YYYY-MM-DD')
      });
    }
  };

  const getMarginColor = (margin) => {
    if (margin >= 40) return 'success';
    if (margin >= 20) return 'warning';
    return 'error';
  };

  const getROIColor = (roi) => {
    if (roi >= 50) return 'success';
    if (roi >= 25) return 'warning';
    return 'error';
  };

  // Calculate totals
  const totals = filteredData.reduce(
    (acc, item) => ({
      invested: acc.invested + item.total_invested,
      revenue: acc.revenue + item.total_revenue,
      profit: acc.profit + item.gross_profit,
      units: acc.units + item.units_sold
    }),
    { invested: 0, revenue: 0, profit: 0, units: 0 }
  );

  const avgMargin = totals.invested > 0 ? ((totals.profit / totals.invested) * 100) : 0;
  const avgROI = totals.invested > 0 ? ((totals.profit / totals.invested) * 100) : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Date Filter and Search */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <DateInterval
            startDate={dateRange.date_from}
            endDate={dateRange.date_to}
            onChange={handleDateChange}
          />
        </Box>
        <TextField
          placeholder="Buscar producto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Summary Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Inversión Total"
            value={totals.invested}
            icon={TrendingUp}
            color="primary"
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ingresos Totales"
            value={totals.revenue}
            icon={TrendingUp}
            color="success"
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ganancia Bruta"
            value={totals.profit}
            icon={TrendingUp}
            color="info"
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Margen Promedio"
            value={avgMargin}
            icon={TrendingUp}
            color="warning"
            format="percentage"
          />
        </Grid>
      </Grid>

      {/* Profitability Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Análisis de Rentabilidad por Producto
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ganancia, margen y ROI de cada producto
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Invertido</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ingresos</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ganancia</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Margen %</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>ROI %</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Unidades Vendidas</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Stock Actual</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No hay datos disponibles
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((product) => (
                  <TableRow key={product.article_id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.sku} • {product.category_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.total_invested)}
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.total_revenue)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: product.gross_profit >= 0 ? 'success.main' : 'error.main'
                        }}
                      >
                        {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.gross_profit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(product.profit_margin || 0).toFixed(1)}%`}
                        size="small"
                        color={getMarginColor(product.profit_margin || 0)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(product.roi || 0).toFixed(1)}%`}
                        size="small"
                        color={getROIColor(product.roi || 0)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2">
                          {product.units_sold}
                        </Typography>
                        {product.units_returned > 0 && (
                          <Typography variant="caption" color="error">
                            -{product.units_returned} devueltos
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={product.current_stock}
                        size="small"
                        color={product.current_stock < 10 ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.stock_value)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ProfitabilityAnalysis;
